import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const PAYMENT_STATUSES = [
  "Payment Pending",
  "Partially Paid",
  "Paid",
  "Waived",
  "Disputed",
  "Uncollectible",
];

const MANUAL_STATUS_OPTIONS = [
  "Payment Pending",
  "Waived",
  "Disputed",
  "Uncollectible",
];

const PAYMENT_METHODS = ["bKash", "Nagad", "Bank", "Cash", "Other"];

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString()} BDT`;
}

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "—";
  }
}

function whatsappHref(number) {
  let digits = String(number || "").replace(/\D/g, "");

  if (digits.startsWith("0")) {
    digits = `880${digits.slice(1)}`;
  }

  // return digits ? `https://wa.me/${digits}` : "#";

  return digits
    ? `https://web.whatsapp.com/send/?phone=${digits}&text&type=phone_number&app_absent=0`
    : "#";
}

function paymentTone(status) {
  if (status === "Paid") return "success";

  if (status === "Disputed" || status === "Uncollectible") {
    return "danger";
  }

  if (status === "Payment Pending") {
    return "warning";
  }

  if (status === "Partially Paid") {
    return "blue";
  }

  return "default";
}

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-slate-50 text-slate-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </span>
  );
}

function ActionButton({ children, onClick, variant = "default", disabled }) {
  const variants = {
    default:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
    dark: "border border-slate-950 bg-slate-950 text-white hover:bg-slate-800",
    success:
      "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-xl px-4 py-2.5 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-50 ${
        variants[variant] || variants.default
      }`}
    >
      {children}
    </button>
  );
}

function MetricCard({ label, value, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-white text-slate-950",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    danger: "border-red-200 bg-red-50 text-red-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm ${
        tones[tone] || tones.default
      }`}
    >
      <p className="text-xs font-black uppercase tracking-wide opacity-70">
        {label}
      </p>

      <p className="mt-3 text-2xl font-black">{value}</p>
    </div>
  );
}

export default function PayTrack({ apiBase, authHeaders, onUnauthorized }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);

  const [summary, setSummary] = useState({
    totalCommissionCollected: 0,
    totalDue: 0,
    disputedUncollectibleWaivedAmount: 0,
    overdueAmount: 0,
    liveApplications: 0,
    closedApplications: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [paymentStatus, setPaymentStatus] = useState("all");

  const [overdueStatus, setOverdueStatus] = useState("all");

  const [paymentMethod, setPaymentMethod] = useState("all");

  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);

  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);

  const [detailsById, setDetailsById] = useState({});

  const [detailsLoadingId, setDetailsLoadingId] = useState(null);

  const [paymentModal, setPaymentModal] = useState(null);

  const [paymentAmount, setPaymentAmount] = useState("");

  const [paymentDate, setPaymentDate] = useState("");

  const [paymentMethodInput, setPaymentMethodInput] = useState("bKash");

  const [externalTransactionId, setExternalTransactionId] = useState("");

  const [paymentNote, setPaymentNote] = useState("");

  const [paymentSaving, setPaymentSaving] = useState(false);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(`${apiBase}/admin/commissions`, {
        headers: authHeaders,

        params: {
          search,
          paymentStatus,
          overdueStatus,
          paymentMethod,
          limit,
          offset,
        },
      });

      setRecords(response.data?.data || []);
      setTotal(Number(response.data?.total || 0));

      setSummary(
        response.data?.summary || {
          totalCommissionCollected: 0,
          totalDue: 0,
          disputedUncollectibleWaivedAmount: 0,
          overdueAmount: 0,
          liveApplications: 0,
          closedApplications: 0,
        },
      );
    } catch (err) {
      console.error("Commission fetch error:", err);

      setError(
        err?.response?.data?.message || "Failed to load commission records",
      );

      if ([401, 403].includes(err?.response?.status)) {
        onUnauthorized?.();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    paymentStatus,
    overdueStatus,
    paymentMethod,
    limit,
    offset,
    authHeaders,
  ]);

  const updateDraft = (recordId, patch) => {
    setDrafts((previous) => ({
      ...previous,

      [recordId]: {
        ...(previous[recordId] || {}),
        ...patch,
      },
    }));
  };

  const saveCommission = async (record) => {
    const recordId = record.commissionRecordId;
    const draft = drafts[recordId] || {};

    const body = {
      confirmed_salary: draft.confirmed_salary ?? record.confirmedSalary,

      confirmation_date:
        draft.confirmation_date ??
        String(record.confirmationDate || "").slice(0, 10),

      reminder_count: draft.reminder_count ?? record.reminderCount,

      admin_note: draft.admin_note ?? record.adminNote ?? "",
    };

    if (draft.payment_status) {
      body.payment_status = draft.payment_status;
    }

    try {
      setSavingId(recordId);

      await axios.patch(`${apiBase}/admin/commissions/${recordId}`, body, {
        headers: authHeaders,
      });

      setDrafts((previous) => {
        const next = { ...previous };
        delete next[recordId];
        return next;
      });

      await fetchCommissions();

      alert("Commission record updated");
    } catch (err) {
      console.error("Commission update error:", err);

      alert(
        err?.response?.data?.message || "Failed to update commission record",
      );
    } finally {
      setSavingId(null);
    }
  };

  const incrementReminder = async (recordId) => {
    try {
      await axios.post(
        `${apiBase}/admin/commissions/${recordId}/reminders`,
        {},
        {
          headers: authHeaders,
        },
      );

      await fetchCommissions();
    } catch (err) {
      alert(
        err?.response?.data?.message || "Failed to increase reminder count",
      );
    }
  };

  const toggleDetails = async (recordId) => {
    if (expandedId === recordId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(recordId);

    try {
      setDetailsLoadingId(recordId);

      const response = await axios.get(
        `${apiBase}/admin/commissions/${recordId}`,
        {
          headers: authHeaders,
        },
      );

      setDetailsById((previous) => ({
        ...previous,
        [recordId]: response.data?.data,
      }));
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load payment history");
    } finally {
      setDetailsLoadingId(null);
    }
  };

  const openPaymentModal = (record) => {
    setPaymentModal(record);
    setPaymentAmount(record.remainingDue || "");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethodInput("bKash");
    setExternalTransactionId("");
    setPaymentNote("");
  };

  const closePaymentModal = () => {
    setPaymentModal(null);
    setPaymentAmount("");
    setPaymentDate("");
    setPaymentMethodInput("bKash");
    setExternalTransactionId("");
    setPaymentNote("");
  };

  const addPayment = async () => {
    if (!paymentModal) return;

    try {
      setPaymentSaving(true);

      await axios.post(
        `${apiBase}/admin/commissions/${paymentModal.commissionRecordId}/payments`,
        {
          amount: Number(paymentAmount),

          payment_date: paymentDate,

          payment_method: paymentMethodInput,

          external_transaction_id:
            paymentMethodInput === "Cash" ? null : externalTransactionId.trim(),

          admin_note: paymentNote.trim() || null,
        },
        {
          headers: authHeaders,
        },
      );

      const recordId = paymentModal.commissionRecordId;

      closePaymentModal();

      setDetailsById((previous) => {
        const next = { ...previous };
        delete next[recordId];
        return next;
      });

      await fetchCommissions();

      alert("Payment added");
    } catch (err) {
      console.error("Payment add error:", err);

      alert(err?.response?.data?.message || "Failed to add payment");
    } finally {
      setPaymentSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setPaymentStatus("all");
    setOverdueStatus("all");
    setPaymentMethod("all");
    setOffset(0);
  };

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-950">PayTrack</h2>

            <p className="mt-1 text-sm leading-6 text-slate-500">
              Track confirmations, commission collection, partial payments,
              reminders, due dates, and overdue accounts.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Pill>Total: {total}</Pill>

            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setOffset(0);
              }}
              className="
    rounded-xl
    border border-slate-200
    bg-white
    px-3 py-2
    text-sm font-bold
    !text-black
    [color-scheme:light]
  "
            >
              <option className="!bg-white !text-black" value={10}>
                10 per page
              </option>
              <option className="!bg-white !text-black" value={20}>
                20 per page
              </option>
              <option className="!bg-white !text-black" value={50}>
                50 per page
              </option>
              <option className="!bg-white !text-black" value={100}>
                100 per page
              </option>
            </select>

            <ActionButton onClick={fetchCommissions}>Refresh</ActionButton>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <MetricCard
            label="Collected"
            value={formatMoney(summary.totalCommissionCollected)}
            tone="success"
          />

          <MetricCard
            label="Total Due"
            value={formatMoney(summary.totalDue)}
            tone="warning"
          />

          <MetricCard
            label="Overdue"
            value={formatMoney(summary.overdueAmount)}
            tone="danger"
          />

          <MetricCard
            label="Waived / Disputed / Lost"
            value={formatMoney(summary.disputedUncollectibleWaivedAmount)}
          />

          <MetricCard
            label="Live Applications"
            value={summary.liveApplications}
            tone="blue"
          />

          <MetricCard
            label="Closed"
            value={summary.closedApplications}
            tone="success"
          />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-5">
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setOffset(0);
                setSearch(searchInput.trim());
              }
            }}
            placeholder="Search tutor, tuition, WhatsApp, transaction ID..."
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold xl:col-span-2"
          />

          <select
            value={paymentStatus}
            onChange={(event) => {
              setPaymentStatus(event.target.value);
              setOffset(0);
            }}
            className="text-black rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold"
          >
            <option value="all">All payment statuses</option>

            {PAYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={overdueStatus}
            onChange={(event) => {
              setOverdueStatus(event.target.value);
              setOffset(0);
            }}
            className="text-black rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold"
          >
            <option value="all">All due conditions</option>
            <option value="overdue">Overdue only</option>
            <option value="not_overdue">Not overdue</option>
          </select>

          <select
            value={paymentMethod}
            onChange={(event) => {
              setPaymentMethod(event.target.value);
              setOffset(0);
            }}
            className="text-black rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold"
          >
            <option value="all">All payment methods</option>

            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <ActionButton
            variant="dark"
            onClick={() => {
              setOffset(0);
              setSearch(searchInput.trim());
            }}
          >
            Search
          </ActionButton>

          <ActionButton onClick={clearFilters}>Clear</ActionButton>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-black ounded-xl border border-slate-200 bg-slate-50 p-6 text-sm font-bold text-slate-500">
              Loading commission records...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
              {error}
            </div>
          ) : records.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">
              No commission records found.
            </div>
          ) : (
            <div className="space-y-4">
              {records.map((record) => {
                const recordId = record.commissionRecordId;

                const draft = drafts[recordId] || {};

                const details = detailsById[recordId];

                return (
                  <div
                    key={recordId}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Pill tone="blue">Commission #{recordId}</Pill>

                          <Pill tone={paymentTone(record.paymentStatus)}>
                            {record.paymentStatus}
                          </Pill>

                          {record.overdueStatus === "Overdue" ? (
                            <Pill tone="danger">Overdue</Pill>
                          ) : null}

                          <Pill>Application #{record.applicationId}</Pill>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <h3 className="font-black text-slate-950">
                              {" "}
                              {record.studentName ||
                                record.tuitionName ||
                                "Unnamed tuition"}
                            </h3>
                            <p className="mt-0.5 text-xs font-semibold text-slate-500">
                              Tuition #{record.tuitionId}
                            </p>

                            <Link
                              to={`/tuition/${record.tuitionId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 inline-block text-xs font-black text-blue-600"
                            >
                              View tuition
                            </Link>

                            <p className="mt-2 text-sm text-slate-500">
                              Subjects: {record.subjects || "—"}
                            </p>

                            <p className="mt-1 text-sm text-slate-500">
                              Area: {record.areas || "—"}
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {record.tuitionPhone ? (
                                <a
                                  href={`tel:${record.tuitionPhone}`}
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                                >
                                  Guardian: {record.tuitionPhone}
                                </a>
                              ) : null}

                              {record.tuitionWhatsapp ? (
                                <a
                                  href={whatsappHref(record.tuitionWhatsapp)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"
                                >
                                  Guardian WhatsApp
                                </a>
                              ) : null}
                            </div>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <h3 className="font-black text-slate-950">
                              {record.tutorName || "Unnamed Tutor"}
                            </h3>

                            <Link
                              to={`/tutor/${record.tutorId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 inline-block text-xs font-black text-blue-600"
                            >
                              Tutor #{record.tutorId}
                            </Link>

                            <p className="mt-2 text-sm text-slate-500">
                              {record.tutorEmail || "—"}
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {record.tutorPhone ? (
                                <a
                                  href={`tel:${record.tutorPhone}`}
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                                >
                                  Tutor: {record.tutorPhone}
                                </a>
                              ) : null}

                              {record.tutorWhatsapp ? (
                                <a
                                  href={whatsappHref(record.tutorWhatsapp)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"
                                >
                                  Tutor WhatsApp
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {!["Paid", "Waived", "Uncollectible"].includes(
                          record.paymentStatus,
                        ) ? (
                          <ActionButton
                            variant="success"
                            onClick={() => openPaymentModal(record)}
                          >
                            Add Payment
                          </ActionButton>
                        ) : null}

                        <ActionButton
                          onClick={() => incrementReminder(recordId)}
                        >
                          + Reminder
                        </ActionButton>

                        <ActionButton onClick={() => toggleDetails(recordId)}>
                          {expandedId === recordId
                            ? "Hide History"
                            : "Payment History"}
                        </ActionButton>

                        <ActionButton
                          variant="dark"
                          disabled={savingId === recordId}
                          onClick={() => saveCommission(record)}
                        >
                          {savingId === recordId ? "Saving..." : "Save"}
                        </ActionButton>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Salary
                        </p>
                        <p className="mt-1 font-black">
                          {formatMoney(record.confirmedSalary)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Commission
                        </p>
                        <p className="mt-1 font-black">
                          {formatMoney(record.commissionAmount)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Paid
                        </p>
                        <p className="mt-1 font-black text-emerald-700">
                          {formatMoney(record.amountPaid)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Remaining
                        </p>
                        <p className="mt-1 font-black text-red-700">
                          {formatMoney(record.remainingDue)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Confirmed
                        </p>
                        <p className="mt-1 font-black">
                          {formatDate(record.confirmationDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Due
                        </p>
                        <p className="mt-1 font-black">
                          {formatDate(record.dueDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-black uppercase text-slate-400">
                          Reminders
                        </p>
                        <p className="mt-1 font-black">
                          {record.reminderCount || 0}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-4">
                      <div>
                        <label className="text-xs font-black uppercase text-slate-400">
                          Confirmed Salary
                        </label>

                        <input
                          type="number"
                          min="1"
                          value={
                            draft.confirmed_salary ?? record.confirmedSalary
                          }
                          onChange={(event) =>
                            updateDraft(recordId, {
                              confirmed_salary: event.target.value,
                            })
                          }
                          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase text-slate-400">
                          Confirmation Date
                        </label>

                        <input
                          type="date"
                          value={
                            draft.confirmation_date ??
                            String(record.confirmationDate || "").slice(0, 10)
                          }
                          onChange={(event) =>
                            updateDraft(recordId, {
                              confirmation_date: event.target.value,
                            })
                          }
                          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase text-slate-400">
                          Change Status
                        </label>

                        <select
                          value={draft.payment_status || ""}
                          onChange={(event) =>
                            updateDraft(recordId, {
                              payment_status: event.target.value,
                            })
                          }
                          className="text-black mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold"
                        >
                          <option value="">Keep current status</option>

                          {MANUAL_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-black uppercase text-slate-400">
                          Reminder Count
                        </label>

                        <input
                          type="number"
                          min="0"
                          value={
                            draft.reminder_count ?? record.reminderCount ?? 0
                          }
                          onChange={(event) =>
                            updateDraft(recordId, {
                              reminder_count: event.target.value,
                            })
                          }
                          className="text-black mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="text-xs font-black uppercase text-slate-400">
                        Admin Note
                      </label>

                      <textarea
                        rows={3}
                        value={draft.admin_note ?? record.adminNote ?? ""}
                        onChange={(event) =>
                          updateDraft(recordId, {
                            admin_note: event.target.value,
                          })
                        }
                        className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold"
                      />
                    </div>

                    {expandedId === recordId && (
                      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <h4 className="font-black text-slate-950">
                          Payment History
                        </h4>

                        {detailsLoadingId === recordId ? (
                          <p className="mt-3 text-sm font-bold text-slate-500">
                            Loading payment history...
                          </p>
                        ) : !details?.payments?.length ? (
                          <p className="mt-3 text-sm font-bold text-slate-500">
                            No payment entries yet.
                          </p>
                        ) : (
                          <div className="mt-3 space-y-3">
                            {details.payments.map((payment) => (
                              <div
                                key={payment.id}
                                className="rounded-xl border border-slate-200 bg-white p-4"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <p className="font-black text-slate-950">
                                      {formatMoney(payment.amount)}
                                    </p>

                                    <p className="mt-1 text-xs font-semibold text-slate-500">
                                      {payment.payment_method} •{" "}
                                      {formatDate(payment.payment_date)}
                                    </p>
                                  </div>

                                  <div className="text-right text-xs font-bold text-slate-500">
                                    <p>{payment.internal_transaction_id}</p>

                                    <p className="mt-1">
                                      {payment.external_transaction_id ||
                                        "No external ID"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <ActionButton
            disabled={offset <= 0}
            onClick={() => setOffset((current) => Math.max(0, current - limit))}
          >
            Previous
          </ActionButton>

          <p className="text-sm font-semibold text-slate-500">
            Showing{" "}
            <span className="font-black text-slate-900">
              {total === 0 ? 0 : offset + 1}
            </span>
            –
            <span className="font-black text-slate-900">
              {Math.min(offset + limit, total)}
            </span>{" "}
            of <span className="font-black text-slate-900">{total}</span>
          </p>

          <ActionButton
            disabled={offset + limit >= total}
            onClick={() => setOffset((current) => current + limit)}
          >
            Next
          </ActionButton>
        </div>
      </section>

      {paymentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
          onClick={closePaymentModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Add Commission Payment
                </h2>

                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Commission #{paymentModal.commissionRecordId}
                </p>
              </div>

              <button
                type="button"
                onClick={closePaymentModal}
                className="rounded-xl border border-slate-200 px-3 py-2 font-black text-slate-500"
              >
                ✕
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-sm font-black">Amount</label>

                <input
                  type="number"
                  min="1"
                  max={paymentModal.remainingDue}
                  value={paymentAmount}
                  onChange={(event) => setPaymentAmount(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold"
                />

                <p className="mt-1 text-xs font-semibold text-slate-400">
                  Remaining due: {formatMoney(paymentModal.remainingDue)}
                </p>
              </div>

              <div>
                <label className="text-sm font-black">Payment Date</label>

                <input
                  type="date"
                  value={paymentDate}
                  onChange={(event) => setPaymentDate(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold"
                />
              </div>

              <div>
                <label className="text-sm font-black">Payment Method</label>

                <select
                  value={paymentMethodInput}
                  onChange={(event) =>
                    setPaymentMethodInput(event.target.value)
                  }
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold"
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              {paymentMethodInput !== "Cash" && (
                <div>
                  <label className="text-sm font-black">Transaction ID</label>

                  <input
                    value={externalTransactionId}
                    onChange={(event) =>
                      setExternalTransactionId(event.target.value)
                    }
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-black">Admin Note</label>

                <textarea
                  rows={3}
                  value={paymentNote}
                  onChange={(event) => setPaymentNote(event.target.value)}
                  className="mt-2 w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold"
                />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <ActionButton onClick={closePaymentModal}>Cancel</ActionButton>

              <ActionButton
                variant="dark"
                disabled={
                  paymentSaving ||
                  !paymentAmount ||
                  !paymentDate ||
                  (paymentMethodInput !== "Cash" &&
                    !externalTransactionId.trim())
                }
                onClick={addPayment}
              >
                {paymentSaving ? "Adding..." : "Add Payment"}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
